import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { AUTH_CB_UCID, AUTH_COOKIE_NAME, AuthCookieValue, MILLISECOND_MULTIPLIER, StorageToken } from 'core/constants';
import { Util } from '../util';
import { LocalStorage } from '../storage/local';
import { SessionStorage } from '../storage/session';

const ONE_MINUTE: number = 60;

@Injectable({
    providedIn: 'root'
})
export class CimaToken {
    public code: string;
    public accessToken: string;
    public refreshToken: string;
    public idToken: string;
    public decodedIdToken: JWT;
    public loginChange: Observable<boolean>;
    public dataObsevable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private loginChangeSubject: BehaviorSubject<boolean>;
    private expirationTimeWithOffset: number;

    private localStorage: LocalStorage;

    constructor(localStorage: LocalStorage, sessionStorage: SessionStorage) {
        Object.assign(this, { localStorage, sessionStorage });

        this.loginChangeSubject = new BehaviorSubject(false);

        this.loginChange = this.loginChangeSubject.pipe(distinctUntilChanged());
        this.loadTokens();
    }

    public get tokens(): CimaTokens {

        return {
            accessToken: this.accessToken,
            refreshToken: this.refreshToken,
            expirationTime: this.expirationTimeWithOffset,
            idToken: this.idToken
        };
    }

    public saveNewToken(token: string, expirationTimeInSeconds: number, refreshToken: string, idToken: string): void {
        this.code = ''; // whenever you set a new token, expire the existing code
        this.accessToken = token;
        this.refreshToken = refreshToken;
        this.idToken = idToken;
        this.decodedIdToken = Util.decodeJWT(idToken);

        const expirationTime: number = Date.now() + (expirationTimeInSeconds - ONE_MINUTE) * MILLISECOND_MULTIPLIER;

        // an effort to offset the multiple tabs from updating the same refresh token
        this.expirationTimeWithOffset = expirationTime - this.generateRandomOffset();
        this.localStorage.set(StorageToken.CIMA, JSON.stringify({
            accessToken: this.accessToken,
            refreshToken: this.refreshToken,
            idToken: this.idToken,
            expirationTime
        }));

        if (localStorage.getItem(StorageToken.CIMA) ) {
            this.onDataReceived(true);
        }

        this.localStorage.set(AUTH_CB_UCID, this.getCookie(AUTH_CB_UCID));
        this.createCookie(AUTH_COOKIE_NAME, AuthCookieValue.SET);
        this.loginChangeSubject.next(true);
    }

    public onDataReceived(status: boolean): void {
        this.dataObsevable.next(status);
    }

    public getData(): Observable<boolean> {
        return this.dataObsevable;
    }
    
    public logoutCleanup(): void {
        this.createCookie(AUTH_COOKIE_NAME, AuthCookieValue.UNSET);
        this.localStorage.destroy(StorageToken.CIMA);
    }

    // TODO: Get complete coverage of this method.
    public get isLoggedIn(): boolean {
        // check a token exist and the token has not expired
        const localStorageCbucid = this.localStorage.get(AUTH_CB_UCID);

        const cookieCbucid = this.getCookie(AUTH_CB_UCID);

        const sameCbUcidLocalStorageAndCookie = cookieCbucid === localStorageCbucid;


        if (cookieCbucid) {
            if (!localStorageCbucid) {
                // check if logged in through my account, but no cb_ucid in localstorage.

                return false;
            } else if (sameCbUcidLocalStorageAndCookie && localStorageCbucid !== '' ) {
                // check if logged in through any flow, and buy app synced with my account
                return this.hasAccess();
            }
        } else {
            // check if logged in through buy flow
            return this.hasAccess();
        }

        return false;
        // return this.accessToken && !this.isExpired();
    }

    public checkForNewTokens(): boolean {
        const tokens: CimaTokens = this.localStorage.get<CimaTokens>(StorageToken.CIMA);
        if (tokens && tokens.accessToken && this.accessToken !== tokens.accessToken) {
            this.loadTokens();

            return true;
        }

        return false;
    }

    public isExpired(tokens?: CimaTokens): boolean {
        const expirationTime: number = tokens ? tokens.expirationTime : this.expirationTimeWithOffset;

        if (!expirationTime) {

            return true;
        }

        const now = Date.now();

        //expiration time should be greater than now if token is valid
        return expirationTime < now;
    }


    public createCookie(name: string, value: string, days: number = 1): void {
        let cookie: string;
        const date: Date = new Date();
        const baseDomain: string = window.location.hostname.split('.').slice(-2).join('.');

        date.setTime(date.getTime() + ((days) * 864e5));
        const expires: string = date.toUTCString();
        cookie = `${encodeURIComponent(name)}=${value};expires=${expires};path=/`;

        if (baseDomain.includes('.')) {
            cookie += ';domain=' + baseDomain;
        }

        document.cookie = cookie;
    }

    public removeCookie(name: string): void {
        if (this.checkCookieExists(name)) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        } 
    }

    public checkCookieExists(name: string): boolean {
        return document.cookie.split(';').some(cookie => {
            
            const [cookieName] = cookie.trim().split('=');
            
            return cookieName === name;
        });
    }

    public getCookie(cname: string): string {
        const name = cname + '=';
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.startsWith(' ')) {
                c = c.substring(1);
            }
            if (c.startsWith(name)) {
                return c.substring(name.length, c.length);
            }
        }

        return '';
    }

    private loadTokens(): void {
        const search: UrlQueries = Util.getObjectFromQueryString(window.location.search.slice(1));

        // first time coming back from CIMA with code
        if (search.code) {
            this.code = search.code;

            return;
        }

        // Otherwise get them from localstorage
        const tokens: CimaTokens = this.localStorage.get<CimaTokens>(StorageToken.CIMA);
        if (!this.isExpired(tokens)) {
            this.accessToken = tokens.accessToken;
            this.refreshToken = tokens.refreshToken;
            this.idToken = tokens.idToken;
            this.decodedIdToken = Util.decodeJWT(tokens.idToken);
            this.expirationTimeWithOffset = tokens.expirationTime - this.generateRandomOffset();

            this.loginChangeSubject.next(true);
        }
    }

    private generateRandomOffset(): number {
        // possible values are from 0-20 (in milliseconds)
        return Math.floor(Math.random() * 20) * MILLISECOND_MULTIPLIER;
    }

    private hasAccess(): boolean {
        if (typeof this.accessToken !== 'undefined') {
            return this.accessToken && !this.isExpired();
        }

        return false;
    }
}